import * as React from 'react'

function isRowList(value: unknown): value is { label: unknown; value: unknown }[] {
  return (
    Array.isArray(value) &&
    value.length > 0 &&
    value.every((row) => row !== null && typeof row === 'object' && !Array.isArray(row) && 'label' in row && 'value' in row)
  )
}

/** Readable text for a confirm-card field. Nested objects become lines. Never raw JSON. */
export function formatPreviewValue(value: unknown): string {
  if (value === null || value === undefined) return ''
  if (typeof value !== 'object') return String(value)
  if (isRowList(value)) return value.map((row) => `${formatPreviewValue(row.label)}: ${formatPreviewValue(row.value)}`).join('\n')
  if (Array.isArray(value))
    return value
      .map((item) => formatPreviewValue(item))
      .filter(Boolean)
      .join('\n')
  return Object.entries(value)
    .filter(([, item]) => item !== null && item !== '' && !(Array.isArray(item) && item.length === 0))
    .map(([key, item]) => `${key.replaceAll('_', ' ')}: ${formatPreviewValue(item)}`)
    .join('\n')
}

export function previewEntries(value: Record<string, unknown>): { label: string; value: string }[] {
  if (isRowList(value.rows)) {
    const rows = value.rows.map((row) => ({ label: formatPreviewValue(row.label), value: formatPreviewValue(row.value) }))
    const rest = Object.entries(value)
      .filter(([key, item]) => key !== 'rows' && key !== 'public_url' && key !== 'token' && item !== null && item !== '')
      .map(([key, item]) => ({ label: key.replaceAll('_', ' '), value: formatPreviewValue(item) }))
    return [...rows, ...rest]
  }
  return Object.entries(value)
    .filter(
      ([key, item]) =>
        key !== 'public_url' && key !== 'token' && item !== null && item !== '' && !(Array.isArray(item) && item.length === 0),
    )
    .map(([key, item]) => ({ label: key.replaceAll('_', ' '), value: formatPreviewValue(item) }))
}

/** The frozen card's preview: label/value rows when the server sent them, otherwise one line per field. */
export function Preview({ value }: { value: Record<string, unknown> }) {
  const rows = previewEntries(value)
  return (
    <dl className="grid gap-2 text-sm sm:grid-cols-[160px_1fr]">
      {rows.map((row) => (
        <React.Fragment key={`${row.label}\u0000${row.value}`}>
          <dt className="font-medium text-muted-foreground">{row.label}</dt>
          <dd className="whitespace-pre-wrap break-words">{row.value}</dd>
        </React.Fragment>
      ))}
    </dl>
  )
}
